import { SignJWT } from 'jose';

import {
  REACT_APP_AUTH_CUBES_JWT_KEY,
  REACT_APP_AUTH_CUBES_JWT_ISSUER,
  REACT_APP_AUTH_CUBES_JWT_AUDIENCE
} from '../api/base';

import { getAccessToken, getUsername, getFullname } from './authentication';
import { parseJwt } from './transports';

class JWTGenerator {
  constructor(key = null, issuer = null, audience = null) {
    this.key = key || REACT_APP_AUTH_CUBES_JWT_KEY;
    this.issuer = issuer || REACT_APP_AUTH_CUBES_JWT_ISSUER;
    this.audience = audience || REACT_APP_AUTH_CUBES_JWT_AUDIENCE;

    if (!this.key || !this.issuer || !this.audience) {
      const errorMsg = 'Missing required JWT configuration. ' +
        `Key: ${this.key ? 'OK' : 'MISSING'}, ` +
        `Issuer: ${this.issuer ? 'OK' : 'MISSING'}, ` +
        `Audience: ${this.audience ? 'OK' : 'MISSING'}. ` +
        'Please ensure REACT_APP_AUTH_CUBES_JWT_KEY, REACT_APP_AUTH_CUBES_JWT_ISSUER, ' +
        'and REACT_APP_AUTH_CUBES_JWT_AUDIENCE are set in your environment file and restart the server.';
      throw new Error(errorMsg);
    }

  }
  getCurrentUserData() {
    const accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error('No access token found. User must be logged in.');
    }

    const tokenData = parseJwt(accessToken);
    if (!tokenData) {
      throw new Error('Invalid access token');
    }

    const username = getUsername() || tokenData.sub || tokenData.username || tokenData.userName || '1';
    const fullname = getFullname() || tokenData.name || '1';

    return {
      id_number: username,
      name_ar: tokenData.name_ar || tokenData.nameAr || fullname || '1',
      name_en: tokenData.name_en || tokenData.nameEn || tokenData.name || fullname || '1',
      email: tokenData.email || '1'
    };
  }

  /**
   * Generate JWT token for CUBES authentication
   * @param {Object} userData - User data object (if not provided, uses current logged-in user)
   * @param {string} userData.id_number - User ID number
   * @param {string} userData.name_ar - User name in Arabic
   * @param {string} userData.name_en - User name in English
   * @param {string} userData.email - User email
   * @returns {Promise<string>} - Generated JWT token
   */
  async generateJwtToken(userData = null) {
    const user = userData || this.getCurrentUserData();

    if (typeof crypto === 'undefined' || !crypto.subtle) {
      throw new Error('Web Crypto API is not available. This code requires a browser environment with crypto support.');
    }

    const binaryString = atob(this.key);
    const keyBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      keyBytes[i] = binaryString.charCodeAt(i);
    }

    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const claims = {};
    claims.auth = 'cubes-jwt';
    claims.sub = user.id_number || '1';
    claims.domain = 'local';
    claims.username = user.id_number || '1';
    claims.user_id = user.id_number || '';
    claims.name = user.name_en || '1';
    claims.name_ar = user.name_ar || '1';
    claims.name_en = user.name_en || '1';
    claims.org_name_ar = '';
    claims.org_name_en = '';
    claims.org_type_flag = '';
    claims.org_sector = '';
    claims.email = user.email || '1';
    claims.mobile = '';
    claims.orgId = '';
    claims.roles = ['dev'];
    claims.permissions = ['agent.*'];

    return await this.sign(claims, this.issuer, this.audience, key);
  }

  /**
   * Sign JWT token
   * @param {Object} jwt - Claims object
   * @param {string} iss - Issuer
   * @param {string} aud - Audience
   * @param {Object} key - Secret key
   * @param {string} expiresIn - Expiration time (default: 16h)
   * @returns {Promise<string>} - Signed JWT token
   */
  async sign(jwt, iss, aud, key, expiresIn = '16h') {
    return await new SignJWT(jwt)
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setIssuer(iss)
      .setAudience(aud)
      .setSubject(jwt.username)
      .setExpirationTime(expiresIn)
      .sign(key);
  }
}

export default JWTGenerator;

